import {
  useMutation,
  UseMutationOptions,
  useQueryClient,
} from '@tanstack/react-query';
import { ApiError, tinctApi } from '../../api';
import { invalidateScimQueries } from './invalidate-scim-queries';

export type RevokeScimTokenParams = {
  organizationId: string;
  tokenId: string;
};

export const revokeScimToken = async (
  params: RevokeScimTokenParams,
): Promise<void> => {
  return await tinctApi.scim.revokeScimToken(
    params.organizationId,
    params.tokenId,
  );
};

/**
 * Revokes a SCIM token: the identity provider calling with it is refused from then on. 404 when it
 * is unknown or already revoked.
 */
export const useRevokeScimToken = (
  options?: Omit<
    UseMutationOptions<void, ApiError, RevokeScimTokenParams>,
    'mutationFn'
  >,
) => {
  const { onSuccess, onError, ...restOptions } = options || {};
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: revokeScimToken,
    onSuccess: (...args) => {
      invalidateScimQueries(queryClient, args[1].organizationId, 'tokens');
      onSuccess?.(...args);
    },
    onError: (...args) => {
      // Already revoked, from another tab or by another admin: the list is what is stale.
      if (args[0]?.httpCode === 404) {
        invalidateScimQueries(queryClient, args[1].organizationId, 'tokens');
      }
      onError?.(...args);
    },
    ...restOptions,
  });
};
